import styled from 'styled-components';
import { Skeleton } from '@shared/ui/Skeleton';
import { useGetCounterpartyRiskMapQuery } from '@shared/api';

interface CategoryStat {
  category: string;
  critical: number;
  major: number;
  minor: number;
  total: number;
}

// — карта рисков по контрагенту. Юрист сразу видит, какие
// категории претензий у этого партнёра встречаются чаще всего: например,
// «у ООО Стройком всегда проблемы с ответственностью и сроками».
//
// Источник — все риски из всех его договоров, сгруппированные по категории.
// Отклонённые юристом (decision='rejected') исключены — они не повторятся.

const CATEGORY_LABELS: Record<string, string> = {
  subject: 'Предмет',
  price: 'Цена и расчёты',
  terms: 'Сроки',
  liability: 'Ответственность',
  warranty: 'Гарантии',
  force_majeure: 'Форс-мажор',
  jurisdiction: 'Подсудность',
  other: 'Прочее',
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Summary = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 12px;
`;

const SummaryCell = styled.div<{ $tone?: 'danger' | 'warning' | 'default' }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  & > .num {
    font-size: 20px;
    font-weight: 700;
    color: ${({ $tone, theme }) =>
      $tone === 'danger'
        ? theme.colors.status.danger
        : $tone === 'warning'
          ? theme.colors.status.warning
          : theme.colors.text.primary};
  }
  & > .lab {
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: 11px;
  }
`;

const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const BarRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr 50px;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 13px;
`;

const CategoryLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`;

const BarTrack = styled.div`
  position: relative;
  height: 18px;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 4px;
  overflow: hidden;
  display: flex;
`;

interface SegmentProps {
  $tone: 'danger' | 'warning' | 'default';
  $width: number;
}

const BarSegment = styled.div<SegmentProps>`
  height: 100%;
  width: ${({ $width }) => `${$width}%`};
  background: ${({ $tone, theme }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warning'
        ? theme.colors.status.warning
        : theme.colors.primary};
  transition: width 240ms ease-out;
`;

const Total = styled.div`
  text-align: right;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  text-align: center;
  font-style: italic;
`;

const Legend = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.muted};
  align-items: center;
`;

const Dot = styled.span<{ $tone: 'danger' | 'warning' | 'default' }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
  background: ${({ $tone, theme }) =>
    $tone === 'danger'
      ? theme.colors.status.danger
      : $tone === 'warning'
        ? theme.colors.status.warning
        : theme.colors.primary};
`;

interface Props {
  counterpartyId: string;
}

export function RiskMap({ counterpartyId }: Props) {
  const { data, isLoading } = useGetCounterpartyRiskMapQuery(counterpartyId);

  if (isLoading) {
    return (
      <Wrap>
        <Skeleton $height={68} />
        <Skeleton $height={22} $width="100%" />
        <Skeleton $height={22} $width="100%" />
        <Skeleton $height={22} $width="100%" />
      </Wrap>
    );
  }
  if (!data) return null;

  if (data.total_risks === 0) {
    return (
      <Empty>
        {data.contracts_count === 0
          ? 'У контрагента ещё нет договоров — карта рисков пуста.'
          : 'По договорам этого контрагента LLM не нашла активных рисков.'}
      </Empty>
    );
  }

  // Нормализуем ширину баров относительно самой проблемной категории —
  // визуально проще сравнивать, чем относительно total.
  const maxTotal = Math.max(...data.categories.map((c: CategoryStat) => c.total), 1);

  return (
    <Wrap>
      <Summary>
        <SummaryCell>
          <div className="num">{data.total_risks}</div>
          <div className="lab">Всего рисков</div>
        </SummaryCell>
        <SummaryCell $tone={data.total_critical > 0 ? 'danger' : 'default'}>
          <div className="num">{data.total_critical}</div>
          <div className="lab">Критических</div>
        </SummaryCell>
        <SummaryCell $tone={data.total_major > 0 ? 'warning' : 'default'}>
          <div className="num">{data.total_major}</div>
          <div className="lab">Существенных</div>
        </SummaryCell>
        <SummaryCell>
          <div className="num">{data.total_minor}</div>
          <div className="lab">Формальных</div>
        </SummaryCell>
      </Summary>

      <Bars>
        {data.categories.map((c: CategoryStat) => {
          const widthCrit = (c.critical / maxTotal) * 100;
          const widthMaj = (c.major / maxTotal) * 100;
          const widthMin = (c.minor / maxTotal) * 100;
          return (
            <BarRow key={c.category}>
              <CategoryLabel>{CATEGORY_LABELS[c.category] ?? c.category}</CategoryLabel>
              <BarTrack
                title={`Критических: ${c.critical}, существенных: ${c.major}, формальных: ${c.minor}`}
              >
                {c.critical > 0 && <BarSegment $tone="danger" $width={widthCrit} />}
                {c.major > 0 && <BarSegment $tone="warning" $width={widthMaj} />}
                {c.minor > 0 && <BarSegment $tone="default" $width={widthMin} />}
              </BarTrack>
              <Total>{c.total}</Total>
            </BarRow>
          );
        })}
      </Bars>

      <Legend>
        <span>
          <Dot $tone="danger" />
          Критические
        </span>
        <span>
          <Dot $tone="warning" />
          Существенные
        </span>
        <span>
          <Dot $tone="default" />
          Формальные
        </span>
        <span style={{ marginLeft: 'auto' }}>
          По {data.contracts_count}{' '}
          {data.contracts_count === 1
            ? 'договору'
            : data.contracts_count < 5
              ? 'договорам'
              : 'договорам'}
        </span>
      </Legend>
    </Wrap>
  );
}
